import { Col, Row } from "reactstrap";
import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";

import {
  createEmailAccount,
  createEmailAccountSuccess,
  getEmailAccountList,
  updateEmailAccount,
  updateEmailAccountSuccess,
} from "../../../store/actions";
import AppModal from "../../Common/AppModal";
import { useBranding } from "../../branding/Branding";

// Controls how the outbound "From" name is composed.
//
// Task 24332: the examples are built live from the SIGNED-IN user and the
// business name currently typed into this form, so what you see is what your
// recipients will actually see. Mirrors the composition in
// EmailOutboundController::(outbound send) so the preview cannot drift.
const OUTBOUND_FROM_TYPE_OPTIONS = [
  { value: "1", label: "Business name only" },
  { value: "2", label: "Full name + business" },
  { value: "3", label: "First name + business" },
  { value: "4", label: "Full name only" },
  { value: "5", label: "First name only" },
  { value: "6", label: "Email account setting for outbound email" },
];

const buildFromNameExample = (type, { fullName, firstName, businessName }) => {
  switch (String(type)) {
    case "1":
      return businessName;
    case "2":
      return [fullName, businessName].filter(Boolean).join(", ");
    case "3":
      return [firstName, businessName].filter(Boolean).join(", ");
    case "4":
      return fullName;
    case "5":
      return firstName;
    case "6":
      return businessName;
    default:
      return "";
  }
};

export default function CreateEditSettingsEmailAccount({
  modal,
  setModal,
  toggle,
  isEdit,
  updateData,
}) {
  const dispatch = useDispatch();
  const { isBrandingActive } = useBranding();
  // get from local storage
  const defaultCompanyName = JSON.parse(localStorage.getItem("authUser"))
    ?.get_user_data?.Company_BusinessName;
  // Task 24332 — the signed-in person, for the live From-name preview. The
  // stored auth payload carries the staff FullName (not separate first/last),
  // so the first name is taken as the leading word.
  const signedInFullName =
    JSON.parse(localStorage.getItem("authUser"))?.get_staff_data?.FullName ?? "";
  const signedInFirstName = signedInFullName.trim().split(/\s+/)[0] ?? "";
  const defaultData = {
    Nickname: "",
    EmailProvider_Id: "1",
    OutBoundEmailFromBusinessName: defaultCompanyName ?? "",
    FlagAutoCreateContactPersonUsingEmail: "0",
    FlagAutoCreateCustomerUsingEmailDomainUsingSenderEmail: "0",
    FlagAutoCreateContactPersonUsingRecipientEmail: "0",
    FlagAutoCreateCustomerUsingEmailDomainUsingRecipientEmail: "0",
    FlagPurchaseExtraction: "0",
    FlagOutboundSending: "0",
    OutboundEmailFromType: "1",
  };
  const [createUpdateData, setCreateUpdateData] = useState(defaultData);
  useEffect(() => {
    if (isEdit) return;
    setCreateUpdateData(defaultData);
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    if (isEmpty(updateData)) return;
    setCreateUpdateData({
      ...defaultData,
      Id: updateData?.Id,
      Nickname: updateData?.NickName,
      // EmailProvider_Id: updateData?.EmailProvider_Id,
      OutBoundEmailFromBusinessName: updateData?.OutboundEmailFromBusinessName,
      FlagAutoCreateContactPersonUsingEmail:
        updateData?.FlagAutoCreateContactPersonUsingEmail,
      FlagAutoCreateCustomerUsingEmailDomainUsingSenderEmail:
        updateData?.FlagAutoCreateCustomerUsingEmailDomainUsingSenderEmail,
      FlagAutoCreateContactPersonUsingRecipientEmail:
        updateData?.FlagAutoCreateContactPersonUsingRecipientEmail,
      FlagPurchaseExtraction: updateData?.FlagPurchaseExtraction ?? "0",
      FlagOutboundSending: updateData?.FlagOutboundSending ?? "0",
      OutboundEmailFromType: updateData?.OutboundEmailFromType ?? "1",
    });
  }, [isEdit, updateData]);

  const handleCreateUpdate = (type) => {
    const payload = { ...createUpdateData };
    // Only send the From-name type when outbound sending is enabled.
    if (payload.FlagOutboundSending !== "1") {
      payload.OutboundEmailFromType = null;
    }
    if (type === "create") {
      dispatch(createEmailAccount([payload]));
    }
    if (type === "update") {
      dispatch(updateEmailAccount([payload]));
    }
  };

  const {
    emailAccountsCreateAPI,
    emailAccountsUpdateAPI,
    emailAccountsCreateLoading,
    emailAccountsUpdateLoading,
  } = useSelector((state) => ({
    emailAccountsCreateAPI: state.EmailInbound.emailAccountsCreate,
    emailAccountsUpdateAPI: state.EmailInbound.emailAccountsUpdate,
    emailAccountsCreateLoading: state.EmailInbound.emailAccountsCreateLoading,
    emailAccountsUpdateLoading: state.EmailInbound.emailAccountsUpdateLoading,
  }));

  //Create Email Account response
  useEffect(() => {
    if (isEmpty(emailAccountsCreateAPI)) return;
    if (emailAccountsCreateAPI?.status === 200) {
      toast.success(emailAccountsCreateAPI.message);
      setModal(false);
      dispatch(getEmailAccountList());
    } else {
      toast.error(emailAccountsCreateAPI.message);
    }
    dispatch(createEmailAccountSuccess([]));
  }, [emailAccountsCreateAPI]);

  //Update Email Account response
  useEffect(() => {
    if (isEmpty(emailAccountsUpdateAPI)) return;
    if (emailAccountsUpdateAPI?.status === 200) {
      toast.success(emailAccountsUpdateAPI.message);
      setModal(false);
      dispatch(getEmailAccountList());
    } else {
      toast.error(emailAccountsUpdateAPI.message);
    }
    dispatch(updateEmailAccountSuccess([]));
  }, [emailAccountsUpdateAPI]);
  return (
    <AppModal
      open={modal}
      onClose={toggle}
      title={isEdit ? "Update Email Account" : "Create Email Account"}
      icon={<AlternateEmailOutlinedIcon />}
      headerAccent="indigo"
      size="sm"
      loading={emailAccountsCreateLoading || emailAccountsUpdateLoading}
      loadingText="Saving…"
      submitLabel={isEdit ? "Update" : "Create"}
      submitColor="success"
      onSubmit={() =>
        isEdit ? handleCreateUpdate("update") : handleCreateUpdate("create")
      }
    >
      <Row className="g-3">
            <Col lg={12}>
              <div>
                <div className={"pt-2"}>
                  <div>
                    <label>Email Provider</label>
                    <h6>{isBrandingActive && "CRMit"}</h6>
                  </div>
                  <div className={"mt-2"}>
                    <label>
                      Company Outbound Email address [example:
                      support@crmit.app]
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter Email Address"
                      value={createUpdateData.OutBoundEmailFromBusinessName}
                      onChange={(e) =>
                        setCreateUpdateData({
                          ...createUpdateData,
                          OutBoundEmailFromBusinessName: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className={"mt-3"}>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        role="switch"
                        className="form-check-input"
                        id="flag-outbound-sending"
                        checked={createUpdateData.FlagOutboundSending === "1"}
                        onChange={(e) =>
                          setCreateUpdateData({
                            ...createUpdateData,
                            FlagOutboundSending: e.target.checked ? "1" : "0",
                          })
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="flag-outbound-sending"
                      >
                        Allow outbound email sending
                      </label>
                    </div>
                  </div>
                  {createUpdateData.FlagOutboundSending === "1" && (
                    <div className={"mt-2"}>
                      <label>Outbound Email From Name</label>
                      <select
                        className="form-select"
                        value={createUpdateData.OutboundEmailFromType ?? ""}
                        onChange={(e) =>
                          setCreateUpdateData({
                            ...createUpdateData,
                            OutboundEmailFromType: e.target.value,
                          })
                        }
                      >
                        {OUTBOUND_FROM_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {/* Task 24332 — live preview using the signed-in user and
                          the business name as currently typed above. */}
                      {(() => {
                        const example = buildFromNameExample(
                          createUpdateData.OutboundEmailFromType,
                          {
                            fullName: signedInFullName,
                            firstName: signedInFirstName,
                            businessName:
                              createUpdateData.OutBoundEmailFromBusinessName ||
                              defaultCompanyName ||
                              "",
                          }
                        );
                        if (!example) return null;
                        return (
                          <small className="text-muted">
                            Your emails will show as: <strong>{example}</strong>
                          </small>
                        );
                      })()}
                    </div>
                  )}
                  <div className={"mt-2"}>
                    <label>Nickname</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="NickName"
                      value={createUpdateData.Nickname}
                      onChange={(e) =>
                        setCreateUpdateData({
                          ...createUpdateData,
                          Nickname: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className={"mt-2"}>
                    <input
                      type="checkbox"
                      className="form-check-input me-1"
                      checked={
                        createUpdateData.FlagAutoCreateContactPersonUsingEmail ===
                        "1"
                      }
                      onChange={(e) =>
                        setCreateUpdateData({
                          ...createUpdateData,
                          FlagAutoCreateContactPersonUsingEmail: e.target
                            .checked
                            ? "1"
                            : "0",
                        })
                      }
                    />
                    <label>
                      Auto Create Missing Contact Person using Sender Email
                      Address
                    </label>
                  </div>{" "}
                  <div className={"mt-2"}>
                    <input
                      type="checkbox"
                      className="form-check-input me-1"
                      checked={
                        createUpdateData.FlagAutoCreateContactPersonUsingRecipientEmail ===
                        "1"
                      }
                      onChange={(e) =>
                        setCreateUpdateData({
                          ...createUpdateData,
                          FlagAutoCreateContactPersonUsingRecipientEmail: e
                            .target.checked
                            ? "1"
                            : "0",
                        })
                      }
                    />
                    <label>
                      Auto Create Missing Contact Person using Recipients Email
                      Address
                    </label>
                  </div>
                  <div className={"mt-2"}>
                    <input
                      type="checkbox"
                      className="form-check-input me-1"
                      checked={
                        createUpdateData.FlagAutoCreateCustomerUsingEmailDomainUsingSenderEmail ===
                        "1"
                      }
                      onChange={(e) =>
                        setCreateUpdateData({
                          ...createUpdateData,
                          FlagAutoCreateCustomerUsingEmailDomainUsingSenderEmail:
                            e.target.checked ? "1" : "0",
                        })
                      }
                    />
                    <label>
                      Auto Create Customer using their email domain from sender
                      email address
                    </label>
                  </div>
                  <div className={"mt-2"}>
                    <input
                      type="checkbox"
                      className="form-check-input me-1"
                      checked={
                        createUpdateData.FlagAutoCreateCustomerUsingEmailDomainUsingRecipientEmail ===
                        "1"
                      }
                      onChange={(e) =>
                        setCreateUpdateData({
                          ...createUpdateData,
                          FlagAutoCreateCustomerUsingEmailDomainUsingRecipientEmail:
                            e.target.checked ? "1" : "0",
                        })
                      }
                    />
                    <label>
                      Auto Create Customer using their email domain from
                      recipient email addresses
                    </label>
                  </div>
                  <div className={"mt-2"}>
                    <input
                      type="checkbox"
                      className="form-check-input me-1"
                      checked={createUpdateData.FlagPurchaseExtraction === "1"}
                      onChange={(e) =>
                        setCreateUpdateData({
                          ...createUpdateData,
                          FlagPurchaseExtraction: e.target.checked ? "1" : "0",
                        })
                      }
                    />
                    <label>
                      Allow automatic Purchase (draft) identification and
                      creation from inbound emails
                    </label>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
    </AppModal>
  );
}
